import { useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { Sphere, BALLS } from "./Sphere";
import { scrollToId } from "@/lib/lenis";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.04em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.15, ease: EASE, delay: 0.2 + i * 0.13 }}
    >
      {children}
    </motion.span>
  </span>
);

const Fade = ({ d, children, className }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: d }}>
    {children}
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const [ball, setBall] = useState("game");
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const ballY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const bigX = useTransform(scrollYProgress, [0, 1], ["0%", "-22%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="grain relative min-h-[100svh] overflow-hidden bg-[#0B1D16] text-[#F5F0E8]">
      <img src="/images/pow-night.jpg" alt="Polocrosse players contesting the ball under lights" className="hero-bg-photo absolute inset-0 h-full w-full object-cover object-[center_30%]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1D16]/85 via-[#0B1D16]/40 to-[#0B1D16]/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D16]/70 via-transparent to-[#0B1D16]/10" />
      <motion.div style={{ x: bigX }} aria-hidden className="pointer-events-none absolute -bottom-[4vw] left-0 whitespace-nowrap font-display text-[38vw] font-black leading-none text-outline">
        POW POW
      </motion.div>

      <div className="relative mx-auto max-w-[1400px] px-5 pb-24 pt-32 sm:px-8 lg:pt-[26rem]">
        <div className="max-w-3xl">
          <Fade d={0.1} className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#D99B26]">
            <span className="h-px w-10 bg-[#D99B26]" /> Family owned · Proudly made in Western Australia
          </Fade>
          <h1 className="font-display text-[15vw] font-black uppercase leading-[0.86] tracking-tight sm:text-[12vw] lg:text-[8.6rem] xl:text-[9.6rem]">
            <Line i={0}>The ball</Line>
            <Line i={1}>for every</Line>
            <Line i={2} className="font-serif-i normal-case text-[#D99B26] tracking-normal">chukka.</Line>
          </h1>
          <Fade d={0.85} className="mt-10 max-w-xl text-base leading-relaxed text-[#F5F0E8]/85 sm:text-lg">
            Choose affordable Game, Splatter or Fluorescent balls, made by a small Australian family business for players and families everywhere.
          </Fade>
          <Fade d={1} className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-order-now-button" onClick={() => scrollToId("shop")} className="btn-gold group">
              Shop the balls
              <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
            <button data-testid="hero-our-story-button" onClick={() => scrollToId("story")} className="nav-link text-sm font-semibold text-[#F5F0E8]">
              Our family story
            </button>
          </Fade>
        </div>

        <motion.div style={{ y: ballY }} className="mt-16 lg:mt-20">
          <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 70, damping: 12, delay: 1.1 }} className="h-32 w-32 sm:h-44 sm:w-44">
            <Sphere variant={ball} mx={mx} my={my} />
          </motion.div>
          <div className="mt-6 flex gap-2">
            {BALLS.map((b) => (
              <button
                key={b.id}
                data-testid={`hero-ball-${b.id}-button`}
                onClick={() => setBall(b.id)}
                className={`rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${ball === b.id ? "border-[#D99B26] bg-[#D99B26] text-[#0F1412]" : "border-[#F5F0E8]/30 text-[#F5F0E8]/85 hover:border-[#F5F0E8]"}`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
